// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-SOC-1 — TanStack Query hooks for the site-of-care domain (E-02: components never call api.* directly).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as soc from '../../api/siteOfCare'

export const socKeys = {
  rx: (rxId: string) => ['site-of-care', 'rx', rxId] as const,
  patient: (patientId: string) => ['site-of-care', 'patient', patientId] as const,
  summary: ['site-of-care', 'summary'] as const,
  userAccess: (userId: string) => ['site-of-care', 'user-access', userId] as const,
  facilityMe: ['facility', 'me'] as const,
  facilityRx: (p: { q?: string; facility?: string; page?: number; page_size?: number }) => ['facility', 'rx', p] as const,
}

export function useRxSiteOfCare(rxId: string | undefined) {
  return useQuery({ queryKey: socKeys.rx(rxId ?? ''), queryFn: () => soc.getRxSiteOfCare(rxId!), enabled: !!rxId, staleTime: 30_000 })
}

export function usePinRxSiteOfCare(rxId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (facilityId: string | null) => facilityId === null ? soc.unpinRxSiteOfCare(rxId) : soc.pinRxSiteOfCare(rxId, facilityId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: socKeys.rx(rxId) }) },
  })
}

export function usePatientSiteOfCare(patientId: string | undefined) {
  return useQuery({ queryKey: socKeys.patient(patientId ?? ''), queryFn: () => soc.getPatientSiteOfCare(patientId!), enabled: !!patientId, staleTime: 30_000 })
}

export function useSiteOfCareSummary(enabled = true) {
  return useQuery({ queryKey: socKeys.summary, queryFn: soc.getSiteOfCareSummary, enabled, staleTime: 15_000 })
}

/**
 * Runs ONE batch of the sweep. The caller loops until `done` (see SiteOfCareTab's runResolve),
 * so cache invalidation is left to the caller: invalidating after every batch would refetch the
 * summary ~27 times on a 54k-Rx tenant while the numbers are still mid-flight.
 */
export function useResolveAllSiteOfCare() {
  return useMutation({ mutationFn: (v: { batch?: number; force?: boolean; since?: string } = {}) => soc.resolveAllSiteOfCare(v.batch, v.force, v.since) })
}

/** Invalidate every site-of-care query — call once, after a sweep finishes or is stopped. */
export function useInvalidateSiteOfCare() {
  const qc = useQueryClient()
  return () => { void qc.invalidateQueries({ queryKey: ['site-of-care'] }) }
}

export function useUserFacilityAccess(userId: string, enabled: boolean) {
  return useQuery({ queryKey: socKeys.userAccess(userId), queryFn: () => soc.getUserFacilityAccess(userId), enabled })
}

export function useSetUserFacilityAccess(userId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => soc.setUserFacilityAccess(userId, ids),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: socKeys.userAccess(userId) }) },
  })
}

export function useFacilityMe() {
  return useQuery({ queryKey: socKeys.facilityMe, queryFn: soc.getFacilityMe, staleTime: 60_000 })
}

export function useFacilityRx(p: { q?: string; facility?: string; page?: number; page_size?: number }) {
  return useQuery({ queryKey: socKeys.facilityRx(p), queryFn: () => soc.listFacilityRx(p), placeholderData: prev => prev })
}

export function usePrescriberSiteOfCare(prescriberId: string | undefined) {
  return useQuery({ queryKey: ['site-of-care', 'prescriber', prescriberId ?? ''], queryFn: () => soc.getPrescriberSiteOfCare(prescriberId!), enabled: !!prescriberId, staleTime: 30_000 })
}

export function useFacilityRxDetail(rxId: string | null) {
  return useQuery({ queryKey: ['facility', 'rx', 'detail', rxId ?? ''], queryFn: () => soc.getFacilityRx(rxId!), enabled: !!rxId, staleTime: 15_000 })
}
